import { Link } from 'react-router';
import { SITE_URL } from '../content/types';
import { PageMeta } from '../components/PageMeta';
import { FAQSection } from '../components/FAQSection';
import type { FAQItem } from '../components/FAQSection';
import { MarketingHeader } from '../components/marketing/MarketingHeader';
import { MarketingFooter } from '../components/MarketingFooter';

interface Plan {
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  cta: string;
  to: string;
  popular?: boolean;
}

const plans: Plan[] = [
  {
    name: 'Starter',
    price: '$0',
    period: 'forever',
    description: 'For side projects and getting started.',
    features: ['1 inbox', '5,000 events / month', '1 brand', 'Unlimited team members', 'All widget types', 'API access'],
    cta: 'Get started free',
    to: '/login',
  },
  {
    name: 'Team',
    price: '$49',
    period: '/month',
    description: 'For growing teams that need more.',
    features: ['5 inboxes', '100,000 events / month', '5 brands', 'Advanced analytics', 'AI insights & semantic search', 'Webhook & Zapier automations', 'Priority support'],
    cta: 'Start free trial',
    to: '/login',
    popular: true,
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    period: '',
    description: 'For regulated and large organizations.',
    features: ['Unlimited inboxes & events', 'SSO / SAML', 'BAA / HIPAA-ready', 'Field-level encryption', 'Dedicated support & SLA', 'Custom integrations'],
    cta: 'Contact sales',
    to: '/login',
  },
];

const pricingFAQs: FAQItem[] = [
  { question: 'Is there a free plan?', answer: 'Yes. The Starter plan is free forever and includes 1 inbox, 5,000 events per month, all widget types, and API access. No credit card is required.' },
  { question: 'What counts as an event?', answer: 'An event is a single feedback submission or tracked widget impression. Your plan includes a monthly event allowance, and you can see usage in your dashboard.' },
  { question: 'Do you charge per user or per seat?', answer: 'No. Pollenate prices per organization, not per seat, so you can invite unlimited team members on every plan — including the free Starter plan.' },
  { question: 'Can I change plans at any time?', answer: 'Yes. You can upgrade, downgrade, or cancel at any time. Upgrades take effect immediately and changes are prorated.' },
  { question: 'Do you offer annual billing?', answer: 'Yes. Annual billing is available on the Team plan at a discounted effective rate of $39/month. Enterprise plans are billed on a custom schedule.' },
  { question: 'Do you offer a BAA for HIPAA compliance?', answer: 'Yes. A signed Business Associate Agreement (BAA) is available on Enterprise plans, along with field-level encryption, enforced 2FA, and data residency controls.' },
];

/** Dedicated, indexable pricing page (previously only a landing-page anchor). */
export function PricingPage() {
  const url = `${SITE_URL}/pricing`;

  const productLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Pollenate',
    description: 'Embeddable customer feedback platform with widgets, feedback pages, NPS, CSAT, and AI-powered insights.',
    brand: { '@type': 'Brand', name: 'Pollenate' },
    url: SITE_URL,
    offers: [
      { '@type': 'Offer', name: 'Starter', price: '0', priceCurrency: 'USD', description: '1 inbox, 5,000 events/month, all widget types', url },
      { '@type': 'Offer', name: 'Team', price: '49', priceCurrency: 'USD', description: '5 inboxes, 100,000 events/month, AI insights', url },
      { '@type': 'Offer', name: 'Enterprise', priceCurrency: 'USD', description: 'Custom pricing — unlimited usage, SSO/SAML, BAA/HIPAA', url, availability: 'https://schema.org/InStock' },
    ],
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Pricing', item: url },
    ],
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: pricingFAQs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  return (
    <div className="min-h-screen bg-[#fafafa] dark:bg-neutral-950">
      <PageMeta
        title="Pricing — Pollenate Customer Feedback Platform"
        description="Simple, transparent pricing. Start free with 5,000 events/month, scale to Team at $49/month, or go Enterprise with SSO and a BAA. No per-seat fees."
        url={url}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(productLd).replace(/</g, '\\u003c') }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd).replace(/</g, '\\u003c') }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd).replace(/</g, '\\u003c') }} />

      <MarketingHeader />

      <main id="main-content" className="px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-sm font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">Pricing</p>
          <h1 className="mt-2 text-3xl sm:text-5xl font-bold tracking-tight text-slate-900 dark:text-white">
            Simple, transparent pricing
          </h1>
          <p className="mt-4 text-lg text-slate-600 dark:text-neutral-400 max-w-2xl mx-auto">
            Start free and scale as you grow. Every plan includes unlimited team members — we price per organization, never per seat.
          </p>
        </div>

        <div className="mt-12 max-w-5xl mx-auto grid gap-6 lg:grid-cols-3">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`relative rounded-2xl border bg-white dark:bg-neutral-900 p-8 flex flex-col ${
                plan.popular
                  ? 'border-amber-400 dark:border-amber-500/60 shadow-lg shadow-amber-500/10'
                  : 'border-slate-200 dark:border-neutral-800'
              }`}
            >
              {plan.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 px-3 py-1 text-xs font-semibold text-white">
                  Most popular
                </span>
              )}
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">{plan.name}</h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-neutral-400">{plan.description}</p>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-4xl font-bold text-slate-900 dark:text-white">{plan.price}</span>
                {plan.period && <span className="text-slate-500 dark:text-neutral-400">{plan.period}</span>}
              </div>
              <ul className="mt-6 space-y-3 flex-1">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-600 dark:text-neutral-300">
                    <span className="mt-0.5 text-amber-500" aria-hidden="true">&#10003;</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Link
                to={plan.to}
                className={`mt-8 inline-flex items-center justify-center rounded-lg px-5 py-3 text-sm font-semibold transition-colors ${
                  plan.popular
                    ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600'
                    : 'border border-slate-300 dark:border-neutral-700 text-slate-900 dark:text-white hover:bg-slate-50 dark:hover:bg-neutral-800'
                }`}
              >
                {plan.cta}
              </Link>
            </div>
          ))}
        </div>

        <p className="mt-8 text-center text-sm text-slate-500 dark:text-neutral-400">
          Comparing tools?{' '}
          <Link to="/vs" className="text-amber-600 dark:text-amber-400 hover:underline">
            See how Pollenate stacks up &rarr;
          </Link>
        </p>
      </main>

      <div className="border-t border-slate-200 dark:border-neutral-800">
        <FAQSection faqs={pricingFAQs} id="pricing" injectSchema={false} />
      </div>

      <MarketingFooter />
    </div>
  );
}
